import { useEffect, useState } from 'react';
import { commissionsApi } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

interface Commission {
  commissionId: string;
  affiliateId: string;
  affiliateName?: string;
  affiliateEmail?: string;
  referralCode?: string;
  orderAmount: number;
  commissionAmount: number;
  commissionRate: number;
  status: string;
  createdAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function getInitials(name: string): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export default function OrgPayouts() {
  const [pendingCommissions, setPendingCommissions] = useState<Commission[]>([]);
  const [recentCommissions, setRecentCommissions] = useState<Commission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const fetchData = async () => {
    try {
      const [pendingRes, recentRes] = await Promise.all([
        commissionsApi.list({ status: 'pending', limit: 100 }),
        commissionsApi.list({ limit: 20 }),
      ]);

      if (pendingRes.data?.success) {
        setPendingCommissions(pendingRes.data?.data?.commissions ?? []);
      }
      if (recentRes.data?.success) {
        // Filter to non-pending for recent history
        const all = recentRes.data?.data?.commissions ?? [];
        setRecentCommissions(all.filter((c: Commission) => c.status !== 'pending').slice(0, 10));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprove = async (commissionId: string) => {
    setProcessingIds(prev => new Set(prev).add(commissionId));
    try {
      await commissionsApi.approve(commissionId);
      toast.success('Commission approved');
      fetchData();
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(commissionId);
        return next;
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to approve commission');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(commissionId);
        return next;
      });
    }
  };

  const handleReject = async (commissionId: string) => {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled

    setProcessingIds(prev => new Set(prev).add(commissionId));
    try {
      await commissionsApi.reject(commissionId, reason || undefined);
      toast.success('Commission rejected');
      fetchData();
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(commissionId);
        return next;
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to reject commission');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(commissionId);
        return next;
      });
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkProcessing(true);
    try {
      await commissionsApi.bulkApprove(Array.from(selectedIds));
      toast.success(`${selectedIds.size} commission(s) approved`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to approve commissions');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingCommissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingCommissions.map(c => c.commissionId)));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  // Calculate summary stats
  const totalPending = pendingCommissions.reduce((sum, c) => sum + c.commissionAmount, 0);
  const approvedCount = recentCommissions.filter(c => c.status === 'approved').length;
  const rejectedCount = recentCommissions.filter(c => c.status === 'rejected').length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-bold text-charcoal">Commission Approvals</h2>
        <p className="text-charcoal/60 mt-1">Review and approve affiliate commissions</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-tan" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Pending Approval</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(totalPending)}</p>
            <p className="text-xs text-charcoal/50 mt-1">{pendingCommissions.length} commission{pendingCommissions.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-sage" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Recently Approved</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{approvedCount}</p>
            <p className="text-xs text-charcoal/50 mt-1">Last 20 commissions</p>
          </div>
        </div>
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-rust" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Recently Rejected</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{rejectedCount}</p>
            <p className="text-xs text-charcoal/50 mt-1">Last 20 commissions</p>
          </div>
        </div>
      </div>

      {/* Pending Commissions */}
      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-tan/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClockIcon className="w-5 h-5 text-tan" />
            <h3 className="font-display text-lg font-bold text-charcoal">Pending Commissions</h3>
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkApprove}
              disabled={isBulkProcessing}
              className="px-4 py-2 bg-sage text-warm-white rounded-md font-medium text-sm hover:bg-sage/90 transition-colors flex items-center gap-2"
            >
              <CheckIcon className="w-4 h-4" />
              {isBulkProcessing ? 'Approving...' : `Approve Selected (${selectedIds.size})`}
            </button>
          )}
        </div>
        {pendingCommissions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-sage" />
            </div>
            <p className="text-charcoal/60 font-medium">All caught up!</p>
            <p className="text-sm text-charcoal/40 mt-2">No commissions pending approval</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === pendingCommissions.length && pendingCommissions.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-tan text-sage focus:ring-sage"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Affiliate</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Order Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/10">
                {pendingCommissions.map((commission) => (
                  <tr key={commission.commissionId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(commission.commissionId)}
                        onChange={() => toggleSelect(commission.commissionId)}
                        className="rounded border-tan text-sage focus:ring-sage"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-charcoal flex items-center justify-center text-warm-white text-xs font-semibold">
                          {getInitials(commission.affiliateName || commission.referralCode || '')}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-charcoal">
                            {commission.affiliateName || 'Unknown'}
                          </p>
                          {commission.referralCode && (
                            <code className="text-xs text-rust">{commission.referralCode}</code>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-charcoal/60">
                      {format(new Date(commission.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 font-medium text-charcoal">
                      {formatCurrency(commission.orderAmount)}
                    </td>
                    <td className="px-6 py-4 text-charcoal/60">
                      {(commission.commissionRate * 100).toFixed(0)}%
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-serif text-lg text-sage">
                        {formatCurrency(commission.commissionAmount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(commission.commissionId)}
                          disabled={processingIds.has(commission.commissionId)}
                          className="p-2 text-sage hover:bg-sage/10 rounded-lg transition-colors"
                          title="Approve"
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleReject(commission.commissionId)}
                          disabled={processingIds.has(commission.commissionId)}
                          className="p-2 text-rust hover:bg-rust/10 rounded-lg transition-colors"
                          title="Reject"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-tan/20">
          <h3 className="font-display text-lg font-bold text-charcoal">Recent Activity</h3>
        </div>
        {recentCommissions.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">No recent activity</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Affiliate</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal/60 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/10">
                {recentCommissions.map((commission) => (
                  <tr key={commission.commissionId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-tan flex items-center justify-center text-charcoal text-xs font-semibold">
                          {getInitials(commission.affiliateName || commission.referralCode || '')}
                        </div>
                        <span className="text-sm text-charcoal">
                          {commission.affiliateName || `#${commission.affiliateId.slice(0, 8)}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-charcoal/60">
                      {format(new Date(commission.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 font-serif text-charcoal">
                      {formatCurrency(commission.commissionAmount)}
                    </td>
                    <td className="px-6 py-4">
                      {commission.status === 'approved' ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-sage/15 text-sage">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Approved
                        </span>
                      ) : commission.status === 'rejected' ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-rust/10 text-rust">
                          <ExclamationCircleIcon className="w-3.5 h-3.5" />
                          Rejected
                        </span>
                      ) : commission.status === 'paid' ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-sage/15 text-sage">
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Paid
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-cream text-charcoal">
                          {commission.status}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="rounded-xl bg-cream/50 border border-tan/30 p-6">
        <h4 className="font-medium text-charcoal mb-2">How Commission Approval Works</h4>
        <ul className="text-sm text-charcoal/70 space-y-2">
          <li>1. When a customer completes a purchase through an affiliate link, a commission is created with pending status.</li>
          <li>2. Review each commission and approve or reject it based on your program rules.</li>
          <li>3. Approved commissions enter a hold period (typically 30 days) before becoming available for withdrawal.</li>
          <li>4. Affiliates can then request withdrawals from their available balance.</li>
        </ul>
      </div>
    </div>
  );
}
